import { apiFetch, API_BASE_URL } from './api.service';

/** api_key/token поля приходят замаскированными: '***set***' — задан, '' — не задан. */
export type AiSettings = {
  inference_base_url: string;
  inference_api_key: string;
  llm_model: string;
  embedding_base_url: string;
  embedding_api_key: string;
  embedding_model: string;
  vlm_base_url: string;
  vlm_api_key: string;
  vlm_model: string;
  /** HF-токен (Read) для диаризации pyannote. */
  hf_token: string;
  /** "true" | "false" — бот-раннер включён. */
  bot_runner_enabled: string;
  bot_runner_url: string;
  bot_runner_api_key: string;
};

export type AiSettingsUpdate = Partial<AiSettings>;

/** kind проверки подключения — соответствует группам на форме (LLM/эмбеддинги/VLM). */
export type AiProviderKind = 'inference' | 'embedding' | 'vlm';

export type AiTestPayload = {
  kind: AiProviderKind;
  /** Оверрайды несохранённых значений формы; если не переданы — берутся сохранённые. */
  base_url?: string;
  api_key?: string;
  model?: string;
};

export type AiTestResult = {
  ok: boolean;
  latency_ms?: number;
  error?: string;
};

export type AiModelsPayload = {
  kind: AiProviderKind;
  /** Оверрайды несохранённых значений формы; если не переданы — берутся сохранённые. */
  base_url?: string;
  api_key?: string;
};

export type AiModelsResult = {
  ok: boolean;
  models?: string[];
  error?: string;
};

async function handleJson<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    let message = fallback;
    try {
      const parsed = JSON.parse(text) as { detail?: string };
      if (parsed.detail) message = parsed.detail;
    } catch {
      if (text) message = text;
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

export const aiSettingsService = {
  async getAiSettings(): Promise<AiSettings> {
    const res = await apiFetch(`${API_BASE_URL}/api/admin/ai-settings`);
    return handleJson<AiSettings>(res, 'Не удалось загрузить настройки AI');
  },

  /** Пустая строка в *_api_key = «не менять сохранённый ключ». */
  async updateAiSettings(payload: AiSettingsUpdate): Promise<AiSettings> {
    const res = await apiFetch(`${API_BASE_URL}/api/admin/ai-settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleJson<AiSettings>(res, 'Не удалось сохранить настройки AI');
  },

  async testAiConnection(payload: AiTestPayload): Promise<AiTestResult> {
    const res = await apiFetch(`${API_BASE_URL}/api/admin/ai-settings/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleJson<AiTestResult>(res, 'Не удалось проверить подключение');
  },

  /** Живой список моделей провайдера (GET {base_url}/models). Ключ в ответ не попадает. */
  async fetchProviderModels(payload: AiModelsPayload): Promise<AiModelsResult> {
    const res = await apiFetch(`${API_BASE_URL}/api/admin/ai-settings/models`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleJson<AiModelsResult>(res, 'Не удалось загрузить список моделей');
  },
};
